import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { formatClientNameString } from '../../utils/nameFormatter';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { AlertTriangle, CheckCircle2, RefreshCw, ExternalLink, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => {
  const v = Number(n || 0);
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toFixed(2)}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

/**
 * ReversalWorkQueueCard — Phase 4 work queue for CLP-22 take-backs.
 *
 * Shows reversal events that need biller attention:
 *   - "Open" (default): reversal posted, no later payment event on the claim,
 *     and the biller has not yet acknowledged.
 *   - "Acknowledged": biller marked it as reviewed (with optional note).
 *   - "All": everything for audit.
 *
 * Billers can "Acknowledge" a reversal to remove it from the open queue,
 * optionally attaching a note. The event stays in the ledger; acknowledgement
 * is a UI marker only and does not mutate financials.
 */
export const ReversalWorkQueueCard = ({ onOpenClaim }) => {
  const [rows, setRows] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('open');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(false);

  const [ackDialog, setAckDialog] = useState({ open: false, row: null, note: '' });
  const [ackBusy, setAckBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/reversals/work-queue`, {
        params: { status: statusFilter, limit: 200 },
      });
      setRows(resp.data?.reversals || []);
      setOpenCount(resp.data?.open_count || 0);
    } catch (e) {
      if (e?.response?.status !== 403) {
        toast.error(e?.response?.data?.detail || 'Failed to load reversal queue');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const hay = [
      r.claim_number, r.client_name, r.client_mrn,
      r.payer_name, r.payer_claim_number, r.era_check_number,
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(search.toLowerCase());
  });

  const acknowledge = async () => {
    if (!ackDialog.row) return;
    setAckBusy(true);
    try {
      await axios.post(
        `${API}/reversals/${ackDialog.row.event_id}/acknowledge`,
        { note: ackDialog.note || null },
      );
      toast.success(`Reversal acknowledged for Claim ${ackDialog.row.claim_number || ackDialog.row.claim_id}`);
      setAckDialog({ open: false, row: null, note: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to acknowledge reversal');
    } finally {
      setAckBusy(false);
    }
  };

  const reopen = async (row) => {
    if (!window.confirm(`Reopen reversal for Claim ${row.claim_number || row.claim_id}?`)) return;
    try {
      await axios.post(`${API}/reversals/${row.event_id}/unacknowledge`);
      toast.success('Reversal reopened');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reopen');
    }
  };

  return (
    <Card className="border-l-4 border-l-rose-500" data-testid="reversal-work-queue-card">
      <CardHeader className="cursor-pointer pb-3" onClick={() => setCollapsed((c) => !c)}>
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-rose-600" />
            Reversals & Take-backs
            <Badge
              className={`${openCount > 0 ? 'bg-rose-600' : 'bg-gray-400'} text-white`}
              data-testid="reversal-queue-open-count"
            >
              {openCount} open
            </Badge>
          </span>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="reversal-queue-refresh-btn"
              className="h-8"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      {!collapsed && (
        <CardContent>
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex gap-1 border rounded-md overflow-hidden">
              {['open', 'acknowledged', 'all'].map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  data-testid={`reversal-filter-${s}`}
                  className={`px-3 py-1 text-xs capitalize ${
                    statusFilter === s
                      ? 'bg-rose-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search claim, client, ICN, check #…"
              className="max-w-xs h-8 text-sm"
              data-testid="reversal-queue-search"
            />
          </div>

          {loading && filtered.length === 0 && (
            <div className="text-center py-6 text-sm text-gray-500">Loading…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div
              className="text-center py-6 text-sm text-gray-500"
              data-testid="reversal-queue-empty"
            >
              {statusFilter === 'open'
                ? 'No open reversals — you are all caught up on take-backs.'
                : 'No reversals found for this filter.'}
            </div>
          )}

          {filtered.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="reversal-queue-table">
                <thead className="bg-gray-50 text-xs text-gray-600">
                  <tr>
                    <th className="text-left p-2">Claim</th>
                    <th className="text-left p-2">Client</th>
                    <th className="text-left p-2">DOS</th>
                    <th className="text-left p-2">Payer / ICN</th>
                    <th className="text-right p-2">Reversed</th>
                    <th className="text-left p-2">Reason</th>
                    <th className="text-left p-2">Posted</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-right p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.event_id}
                      className="border-t hover:bg-rose-50/30"
                      data-testid={`reversal-row-${r.event_id}`}
                    >
                      <td className="p-2">
                        <div className="font-mono text-xs font-medium">{r.claim_number || '—'}</div>
                        <div className="text-[10px] text-gray-400 capitalize">{r.claim_status?.replace(/_/g, ' ')}</div>
                      </td>
                      <td className="p-2">
                        <div>{formatClientNameString(r.client_name) || '—'}</div>
                        <div className="text-[10px] text-gray-400 font-mono">{r.client_mrn}</div>
                      </td>
                      <td className="p-2">{fmtDate(r.date_of_service)}</td>
                      <td className="p-2">
                        <div>{r.payer_name || '—'}</div>
                        <div className="text-[10px] text-gray-400 font-mono">
                          {r.payer_claim_number || ''}
                          {r.era_check_number && ` · Check ${r.era_check_number}`}
                        </div>
                      </td>
                      <td className="p-2 text-right font-mono font-semibold text-rose-700">
                        {fmtMoney(r.reversed_amount)}
                      </td>
                      <td className="p-2 capitalize text-xs">
                        {(r.reason_category || 'other').replace(/_/g, ' ')}
                        {r.clp_status_code && <span className="font-mono text-gray-400"> · {r.clp_status_code}</span>}
                      </td>
                      <td className="p-2 text-xs text-gray-600">{fmtDate(r.posted_at)}</td>
                      <td className="p-2">
                        {r.acknowledged_at ? (
                          <div className="flex items-center gap-1 text-emerald-700 text-xs">
                            <CheckCircle2 className="h-3 w-3" />
                            <span title={r.acknowledgement_note || ''}>
                              Ack'd {fmtDate(r.acknowledged_at)}
                            </span>
                          </div>
                        ) : (
                          <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-xs">Open</Badge>
                        )}
                      </td>
                      <td className="p-2 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          {onOpenClaim && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7"
                              onClick={() => onOpenClaim(r.claim_id)}
                              data-testid={`reversal-open-claim-${r.event_id}`}
                            >
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                          )}
                          {!r.acknowledged_at ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => setAckDialog({ open: true, row: r, note: '' })}
                              data-testid={`reversal-ack-btn-${r.event_id}`}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Acknowledge
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => reopen(r)}
                              data-testid={`reversal-reopen-btn-${r.event_id}`}
                            >
                              <RotateCcw className="h-3 w-3 mr-1" /> Reopen
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      )}

      <Dialog open={ackDialog.open} onOpenChange={(open) => !open && setAckDialog({ open: false, row: null, note: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Acknowledge Reversal</DialogTitle>
            <DialogDescription>
              {ackDialog.row && (
                <>
                  Claim <b>{ackDialog.row.claim_number}</b> · {ackDialog.row.client_name} ·{' '}
                  <span className="font-mono text-rose-700">{fmtMoney(ackDialog.row.reversed_amount)}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-sm font-medium">Resolution note (optional)</label>
            <Textarea
              value={ackDialog.note}
              onChange={(e) => setAckDialog((d) => ({ ...d, note: e.target.value }))}
              placeholder="e.g., Re-filed to secondary payer on 2/15"
              className="mt-1"
              data-testid="reversal-ack-note-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAckDialog({ open: false, row: null, note: '' })}
              disabled={ackBusy}
            >
              Cancel
            </Button>
            <Button
              onClick={acknowledge}
              disabled={ackBusy}
              data-testid="reversal-ack-confirm-btn"
            >
              {ackBusy ? 'Saving…' : 'Acknowledge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ReversalWorkQueueCard;
